
import { useState } from 'react';
import { emergencyLocations, locationTypeConfig } from '../../../mocks/emergencyLocations';

type LocationType = 'hospital' | 'fire' | 'police';

export default function EmergencyServicesMap() {
  const [activeFilter, setActiveFilter] = useState<'all' | LocationType>('all');
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);

  const filtered = activeFilter === 'all'
    ? emergencyLocations
    : emergencyLocations.filter((loc) => loc.type === activeFilter);

  const counts = {
    all: emergencyLocations.length,
    hospital: emergencyLocations.filter((l) => l.type === 'hospital').length,
    fire: emergencyLocations.filter((l) => l.type === 'fire').length,
    police: emergencyLocations.filter((l) => l.type === 'police').length,
  };

  const filters: { key: 'all' | LocationType; label: string; icon: string }[] = [
    { key: 'all', label: 'All Facilities', icon: 'ri-map-pin-line' },
    { key: 'hospital', label: 'Hospitals', icon: 'ri-hospital-line' },
    { key: 'fire', label: 'Fire Stations', icon: 'ri-fire-line' },
    { key: 'police', label: 'Police', icon: 'ri-shield-star-line' },
  ];

  const selected = selectedLocation
    ? emergencyLocations.find((l) => l.id === selectedLocation)
    : null;

  return (
    <section id="services-map" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-teal-600 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            <i className="ri-map-pin-2-line"></i>
            Interactive Map
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Emergency Services Near You
          </h2>
          <p className="text-gray-600 text-sm max-w-xl mx-auto">
            Locate hospitals, fire stations, and police departments across Roseville and Placer County
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto mb-10">
          {(['hospital', 'fire', 'police'] as LocationType[]).map((type) => {
            const config = locationTypeConfig[type];
            return (
              <div
                key={type}
                className={`${config.bg} border ${config.border} rounded-xl p-4 text-center cursor-pointer transition-all hover:shadow-md ${activeFilter === type ? 'ring-2 ring-offset-1 ring-teal-400 shadow-md' : ''}`}
                onClick={() => setActiveFilter(activeFilter === type ? 'all' : type)}
              >
                <div className={`w-10 h-10 flex items-center justify-center rounded-full bg-white mx-auto mb-2 shadow-sm`}>
                  <i className={`${config.icon} ${config.color} text-lg`}></i>
                </div>
                <p className="text-2xl font-bold text-gray-900">{counts[type]}</p>
                <p className="text-xs text-gray-500 mt-0.5">{config.label}</p>
              </div>
            );
          })}
        </div>

        {/* Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-8">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => { setActiveFilter(f.key); setSelectedLocation(null); }}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeFilter === f.key
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <i className={`${f.icon} text-base`}></i>
              {f.label}
              <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeFilter === f.key ? 'bg-white/20' : 'bg-gray-100'}`}>
                {counts[f.key]}
              </span>
            </button>
          ))}
        </div>

        {/* Map + Sidebar Layout */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Map */}
          <div className="lg:col-span-2 rounded-2xl overflow-hidden shadow-xl border border-gray-200 h-[540px] relative">
            <iframe
              src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d49800.0!2d-121.31!3d38.7521!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x809b1a8a1b7e1c1d%3A0x1b1b1b1b1b1b1b1b!2sRoseville%2C%20CA!5e0!3m2!1sen!2sus!4v1700000000000`}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen={true}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Roseville Emergency Services Map"
            ></iframe>
            {/* Map Legend Overlay */}
            <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg px-4 py-3 border border-gray-100">
              <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2">Legend</p>
              <div className="flex items-center gap-4">
                {(['hospital', 'fire', 'police'] as LocationType[]).map((type) => {
                  const config = locationTypeConfig[type];
                  return (
                    <div key={type} className="flex items-center gap-1.5">
                      <span className={`w-2.5 h-2.5 rounded-full ${config.dot}`}></span>
                      <span className="text-xs text-gray-600">{type === 'hospital' ? 'Hospital' : type === 'fire' ? 'Fire Station' : 'Police'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar List */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden flex flex-col h-[540px]">
            <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/50 flex-shrink-0">
              <h3 className="font-semibold text-gray-900 text-sm">
                {activeFilter === 'all' ? 'All Facilities' : locationTypeConfig[activeFilter].label}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{filtered.length} locations found</p>
            </div>
            <div className="overflow-y-auto flex-1 divide-y divide-gray-100">
              {filtered.map((loc) => {
                const config = locationTypeConfig[loc.type];
                const isSelected = selectedLocation === loc.id;
                return (
                  <div
                    key={loc.id}
                    onClick={() => setSelectedLocation(isSelected ? null : loc.id)}
                    className={`px-5 py-4 cursor-pointer transition-all hover:bg-gray-50 ${isSelected ? 'bg-teal-50/60 border-l-[3px] border-l-teal-500' : 'border-l-[3px] border-l-transparent'}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-9 h-9 ${config.bg} rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5`}>
                        <i className={`${config.icon} ${config.color} text-base`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-gray-900 text-sm leading-snug truncate">{loc.name}</h4>
                        </div>
                        <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full ${config.badge} mb-2`}>
                          {loc.type === 'hospital' ? 'Medical' : loc.type === 'fire' ? 'Fire' : 'Police'}
                        </span>

                        {/* Expanded Details */}
                        {isSelected && (
                          <div className="mt-2 space-y-2 animate-fadeIn">
                            <p className="text-xs text-gray-500 flex items-start gap-1.5">
                              <i className="ri-map-pin-line mt-0.5 flex-shrink-0"></i>
                              {loc.address}
                            </p>
                            <p className="text-xs text-gray-500 flex items-center gap-1.5">
                              <i className="ri-time-line flex-shrink-0"></i>
                              {loc.hours}
                            </p>
                            <a
                              href={`tel:${loc.phone.replace(/[^0-9]/g, '')}`}
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-teal-600 hover:text-teal-700 transition-colors mt-1"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="ri-phone-fill"></i>
                              {loc.phone}
                            </a>
                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(loc.address)}`}
                              target="_blank"
                              rel="noopener noreferrer nofollow"
                              className="flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-xs font-medium transition-colors mt-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <i className="ri-direction-line"></i>
                              Get Directions
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-gray-400 leading-relaxed max-w-3xl mx-auto">
            <strong>Data Sources:</strong> Facility locations from City of Roseville, Placer County, and respective agency websites. Map data &copy; Google. For emergencies, always call <strong>911</strong>.
          </p>
        </div>
      </div>
    </section>
  );
}
